// Measurability sliver — mock staff + KPIs + badges + leaderboard aggregates.
// Deterministic: same list every load. Portraits via i.pravatar.cc seeded by person id.

import { BRANCHES, COUNTRIES, REGIONS, TOWNS } from './orgData';

// ---------- Seeded PRNG (deterministic mulberry32) ----------
function hashSeed(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function prng(seed) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

const pick = (rng, arr) => arr[Math.floor(rng() * arr.length)];

// ---------- Name pools ----------
const SA_FIRST = [
  'Thabo','Sipho','Anele','Lerato','Thandi','Bongani','Siyabonga','Palesa','Naledi','Kagiso',
  'Mpho','Refiloe','Nokuthula','Musa','Lindiwe','Zanele','Jabu','Tebogo','Phumzile','Zinhle',
  'Themba','Nomvula','Khanyisile','Sibusiso','Dineo','Lungile','Ayanda','Mandla','Zodwa','Pule',
  'Tshepo','Busisiwe','Khosi','Senzo','Noluthando','Mbali','Gift','Tumelo','Bianca','Pieter',
  'Jannie','Willem','Johan','Daniel','Sarah','Anneke','Elize','Carel','Suzaan','Schalk',
  'Riaan','Stefanus','Marlene','Ruan','Francois','Charl','Cobus','Carla','Pierre','Clinton',
  'Zohra','Fatima','Aisha','Imraan','Tariq','Yusuf','Leila','Priya','Raj','Ravi',
  'Anesh','Viren','Kiran','Naren','Sanjay','Deepa','Kamila','Hendrik','Saskia',
];
const SA_LAST = [
  'Maseko','Dlamini','Ndlovu','Khumalo','Mokoena','Nkosi','Zulu','Mthembu','Molefe','Ndaba',
  'Naidoo','Pillay','Moodley','Govender','Reddy','Botha','du Plessis','van Wyk','Viljoen','Nel',
  'Kruger','Swart','Strydom','Pretorius','Visser','Uys','Khan','Patel','Cele','Gumede',
  'Mabaso','Motaung','Nyathi','Moyo','Mogale','Radebe','Mokwena','Makhanya','Tshabalala',
];
const AFRICA_FIRST = [
  'Tapiwa','Farai','Tendai','Blessing','Rudo','Nyasha','Mwangi','Wanjiru','Njoroge','Kamau',
  'Kiptoo','Otieno','Chilufya','Mwale','Banda','Tembo','Kapoko','Shaanika','Keetile','Seisa',
  'Carlos','Manuela','Celestino','Aida','Elisa','Noé','Luisa',
];
const AFRICA_LAST = [
  'Chikomba','Dube','Muchuka','Sibanda','Mhlanga','Mwangi','Njoroge','Otieno','Kamau','Ruto',
  'Chilufya','Mwale','Tembo','Phiri','Banda','Shaanika','Haivera','Kapoko','Tjihumino',
  'Keetile','Seisa','Kgotla','Sitole','Machava','Chissano','Mondlane',
];
const ROLES = [
  { title: 'Branch Manager', weight: 1 },
  { title: 'Operations Supervisor', weight: 1 },
  { title: 'QC Inspector', weight: 1 },
  { title: 'Senior Technician', weight: 2 },
  { title: 'Fitment Technician', weight: 3 },
  { title: 'Apprentice Technician', weight: 1 },
  { title: 'Sales Consultant', weight: 1 },
  { title: 'Admin Lead', weight: 1 },
];

// ---------- Badges ----------
export const BADGES = {
  GOLD_MEDAL:      { id: 'GOLD_MEDAL',      name: 'Gold Medal',       short: 'GOLD',   color: '#FFB800', glyph: '★', desc: 'Top performer nationally this month.' },
  SILVER_MEDAL:    { id: 'SILVER_MEDAL',    name: 'Silver Medal',     short: 'SILVER', color: '#C0C0C0', glyph: '★', desc: 'Second nationally this month.' },
  BRONZE_MEDAL:    { id: 'BRONZE_MEDAL',    name: 'Bronze Medal',     short: 'BRONZE', color: '#CD7F32', glyph: '★', desc: 'Third nationally this month.' },
  STREAK_7:        { id: 'STREAK_7',        name: 'Hot Streak — 7d',  short: 'FIRE 7', color: '#FF6B35', glyph: '♨', desc: 'All targets met, 7 consecutive days.' },
  STREAK_30:       { id: 'STREAK_30',       name: 'Hot Streak — 30d', short: 'FIRE 30',color: '#D71921', glyph: '♨', desc: 'All targets met, 30 consecutive days.' },
  STREAK_90:       { id: 'STREAK_90',       name: 'Hot Streak — 90d', short: 'FIRE 90',color: '#FF1A1A', glyph: '♨', desc: 'Three months of unbroken excellence.' },
  CLEAN_SWEEP:     { id: 'CLEAN_SWEEP',     name: 'Clean Sweep',      short: 'SWEEP',  color: '#00C27A', glyph: '✓', desc: 'Zero defects over a 30 day window.' },
  THE_CLOSER:      { id: 'THE_CLOSER',      name: 'The Closer',       short: 'CLOSER', color: '#D71921', glyph: '◉', desc: 'Highest completion count this month.' },
  IRON_CLIPBOARD:  { id: 'IRON_CLIPBOARD',  name: 'Iron Clipboard',   short: 'IRON',   color: '#8FA3B7', glyph: '▤', desc: '100% report timeliness for 90 days.' },
  PITSTOP:         { id: 'PITSTOP',         name: 'Pitstop',          short: 'PIT',    color: '#FFB800', glyph: '⚑', desc: 'Fastest average fitment time in region.' },
  THE_COACH:       { id: 'THE_COACH',       name: 'The Coach',        short: 'COACH',  color: '#A060FF', glyph: '♜', desc: 'Most training hours + best mentee uplift.' },
  GHOST:           { id: 'GHOST',           name: 'The Ghost',        short: 'GHOST',  color: '#00D4FF', glyph: '◐', desc: 'Perfect attendance, six months running.' },
};

export const BADGE_LIST = Object.values(BADGES);

// ---------- Composite score ----------
const WEIGHTS = {
  targetPct: 0.25,
  slaPct:    0.25,
  reportPct: 0.15,
  ftrPct:    0.15,
  csat:      0.10,
  safety:    0.05,
  attendance:0.05,
};

export function composite(k) {
  const tgt = Math.min(k.targetPct, 130);
  const raw =
    (tgt / 130 * 100) * WEIGHTS.targetPct +
    k.slaPct              * WEIGHTS.slaPct +
    k.reportPct           * WEIGHTS.reportPct +
    k.ftrPct              * WEIGHTS.ftrPct +
    k.csat                * WEIGHTS.csat +
    k.safety              * WEIGHTS.safety +
    k.attendance          * WEIGHTS.attendance;
  return +raw.toFixed(1);
}

// ---------- Per-branch headcount (clipped + deterministic) ----------
function staffCountFor(branch, rng) {
  const hc = branch.hr?.headcount ?? 24;
  const base = Math.max(5, Math.min(9, Math.round(hc / 4)));
  const jitter = Math.floor(rng() * 3) - 1;
  return Math.max(4, base + jitter);
}

function weightedRole(rng, assigned) {
  const pool = ROLES.flatMap(r => Array(r.weight).fill(r.title));
  const managerTaken = assigned.includes('Branch Manager');
  const supervisorTaken = assigned.includes('Operations Supervisor');
  const qcTaken = assigned.includes('QC Inspector');
  const adminTaken = assigned.includes('Admin Lead');
  const filtered = pool.filter(t => {
    if (t === 'Branch Manager' && managerTaken) return false;
    if (t === 'Operations Supervisor' && supervisorTaken) return false;
    if (t === 'QC Inspector' && qcTaken) return false;
    if (t === 'Admin Lead' && adminTaken) return false;
    return true;
  });
  return pick(rng, filtered);
}

// ---------- Generate staff ----------
function genPerson(rng, branch, idx) {
  const isAfrica = branch.country !== 'South Africa';
  const firsts = isAfrica && rng() < 0.55 ? AFRICA_FIRST : SA_FIRST;
  const lasts  = isAfrica && rng() < 0.55 ? AFRICA_LAST  : SA_LAST;
  const first = pick(rng, firsts);
  const last  = pick(rng, lasts);
  const name = `${first} ${last}`;
  const id = `${branch.id}-p${String(idx).padStart(2, '0')}`;

  // Branch performance as a ceiling bias
  const branchSla = branch.ops.slaAdherence;
  const branchFtr = branch.ops.firstTimeFix;
  const bias = (branchSla - 85) / 15; // -0.3 to +1.0 range roughly

  const n = () => (rng() - 0.5);
  const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));

  const kpis = {
    targetPct:  +clamp(88 + bias * 12 + n() * 22, 55, 135).toFixed(1),
    slaPct:     +clamp(branchSla + n() * 10, 60, 100).toFixed(1),
    reportPct:  +clamp(85 + bias * 8 + n() * 20, 40, 100).toFixed(0),
    ftrPct:     +clamp(branchFtr + n() * 8,  65, 100).toFixed(1),
    csat:       +clamp(88 + bias * 6 + n() * 12, 55, 100).toFixed(0),
    safety:     +clamp(95 + n() * 8, 70, 100).toFixed(0),
    attendance: +clamp(95 + n() * 10, 72, 100).toFixed(0),
    trainingHrs:Math.round(clamp(18 + bias * 10 + n() * 20, 0, 60)),
  };

  const score = composite(kpis);
  const streakDays = score > 92 ? Math.round(rng() * 80 + 15) : score > 82 ? Math.round(rng() * 25) : Math.round(rng() * 6);
  const deltaWeek  = Math.round(n() * 10);
  const tenureYears = Math.round(1 + rng() * 18);
  const joinedYear  = 2026 - tenureYears;

  return {
    id, name, branchId: branch.id,
    townName: branch.town, regionName: branch.region, countryName: branch.country,
    peerGroup: branch.peerGroup,
    role: null, // filled below after dedupe
    portraitUrl: `https://i.pravatar.cc/160?u=${id}`,
    kpis,
    score,
    streakDays,
    deltaWeek,
    tenureYears,
    joinedYear,
    badges: [], // filled in second pass
  };
}

function generateAll() {
  const staff = [];
  BRANCHES.forEach((b) => {
    const rng = prng(hashSeed(`staff::${b.id}`));
    const count = staffCountFor(b, rng);
    const assignedRoles = [];
    for (let i = 0; i < count; i++) {
      const p = genPerson(rng, b, i);
      const role = weightedRole(rng, assignedRoles);
      assignedRoles.push(role);
      p.role = role;
      staff.push(p);
    }
  });
  return staff;
}

const STAFF_RAW = generateAll();

// ---------- Rankings + badge assignment ----------
const byScoreDesc = [...STAFF_RAW].sort((a, b) => b.score - a.score);

byScoreDesc.forEach((p, idx) => {
  p.rankNational = idx + 1;
});

// Rank within region + branch
const rankWithin = (keyFn) => {
  const groups = {};
  STAFF_RAW.forEach(p => {
    const k = keyFn(p);
    if (!groups[k]) groups[k] = [];
    groups[k].push(p);
  });
  Object.values(groups).forEach(arr => {
    arr.sort((a, b) => b.score - a.score).forEach((p, i) => {
      if (keyFn === keyRegion)  p.rankRegion  = i + 1;
      if (keyFn === keyBranch)  p.rankBranch  = i + 1;
      if (keyFn === keyCountry) p.rankCountry = i + 1;
    });
  });
};
const keyCountry = p => p.countryName;
const keyRegion  = p => `${p.countryName}::${p.regionName}`;
const keyBranch  = p => p.branchId;
rankWithin(keyCountry);
rankWithin(keyRegion);
rankWithin(keyBranch);

// Badge assignment
byScoreDesc.forEach((p, idx) => {
  const badges = [];
  if (idx === 0) badges.push('GOLD_MEDAL');
  else if (idx === 1) badges.push('SILVER_MEDAL');
  else if (idx === 2) badges.push('BRONZE_MEDAL');

  if (p.streakDays >= 90) badges.push('STREAK_90');
  else if (p.streakDays >= 30) badges.push('STREAK_30');
  else if (p.streakDays >= 7)  badges.push('STREAK_7');

  if (p.kpis.ftrPct >= 97 && p.kpis.safety >= 98) badges.push('CLEAN_SWEEP');
  if (p.kpis.reportPct === 100 && p.kpis.slaPct >= 95) badges.push('IRON_CLIPBOARD');
  if (p.kpis.targetPct >= 120) badges.push('THE_CLOSER');
  if (p.kpis.attendance === 100 && p.tenureYears >= 4) badges.push('GHOST');
  if (p.kpis.trainingHrs >= 45 && p.role.includes('Senior')) badges.push('THE_COACH');

  p.badges = [...new Set(badges)];
});

// Per-region PITSTOP — fastest SLA in each region
const regionGroups = {};
STAFF_RAW.forEach(p => {
  const k = `${p.countryName}::${p.regionName}`;
  if (!regionGroups[k]) regionGroups[k] = [];
  regionGroups[k].push(p);
});
Object.values(regionGroups).forEach(group => {
  const fastest = [...group].sort((a, b) => b.kpis.slaPct - a.kpis.slaPct)[0];
  if (fastest && !fastest.badges.includes('PITSTOP')) fastest.badges.push('PITSTOP');
});

export const STAFF = STAFF_RAW;

export const STAFF_BY_BRANCH = STAFF.reduce((acc, p) => {
  if (!acc[p.branchId]) acc[p.branchId] = [];
  acc[p.branchId].push(p);
  return acc;
}, {});

// ---------- Scope aggregates ----------
function aggregate(peopleList) {
  if (peopleList.length === 0) return { score: 0, topPersonId: null, headcount: 0, onTargetPct: 0, slaAvg: 0, reportAvg: 0, badgeCount: 0, breachCount: 0 };
  const scoreAvg   = peopleList.reduce((s, p) => s + p.score, 0) / peopleList.length;
  const slaAvg     = peopleList.reduce((s, p) => s + p.kpis.slaPct, 0) / peopleList.length;
  const reportAvg  = peopleList.reduce((s, p) => s + p.kpis.reportPct, 0) / peopleList.length;
  const onTarget   = peopleList.filter(p => p.kpis.targetPct >= 100).length;
  const badgeCount = peopleList.reduce((s, p) => s + p.badges.length, 0);
  const breachCount= peopleList.filter(p => p.kpis.slaPct < 85 || p.kpis.targetPct < 85).length;
  const topPerson  = [...peopleList].sort((a, b) => b.score - a.score)[0];
  return {
    score: +scoreAvg.toFixed(1),
    slaAvg: +slaAvg.toFixed(1),
    reportAvg: +reportAvg.toFixed(1),
    onTargetPct: Math.round(onTarget / peopleList.length * 100),
    headcount: peopleList.length,
    badgeCount,
    breachCount,
    topPersonId: topPerson?.id ?? null,
  };
}

export const GROUP_MEASURE = aggregate(STAFF);

export const BRANCH_MEASURE = BRANCHES.reduce((acc, b) => {
  acc[b.id] = {
    id: b.id, name: b.name, town: b.town, region: b.region, country: b.country,
    ...aggregate(STAFF_BY_BRANCH[b.id] || []),
  };
  return acc;
}, {});

export const TOWN_MEASURE = TOWNS.map(t => ({
  id: t.id, name: t.name, region: t.region, country: t.country,
  ...aggregate(STAFF.filter(p => p.townName === t.name && p.regionName === t.region && p.countryName === t.country)),
}));

export const REGION_MEASURE = REGIONS.map(r => ({
  id: r.id, name: r.name, country: r.country,
  ...aggregate(STAFF.filter(p => p.regionName === r.name && p.countryName === r.country)),
}));

export const COUNTRY_MEASURE = COUNTRIES.map(c => ({
  id: c.id, name: c.name,
  ...aggregate(STAFF.filter(p => p.countryName === c.name)),
}));

// ---------- Leaderboards ----------
export const TOP_NATIONAL = [...STAFF].sort((a, b) => b.score - a.score).slice(0, 12);
export const TOP_MOVERS   = [...STAFF].sort((a, b) => b.deltaWeek - a.deltaWeek).slice(0, 6);
export const TOP_STREAKS  = [...STAFF].sort((a, b) => b.streakDays - a.streakDays).slice(0, 6);

// Staff with most badges
export const MOST_DECORATED = [...STAFF].sort((a, b) => b.badges.length - a.badges.length).slice(0, 6);

// Bottom performers (for Wall of Shame — but branch-level, not face-level)
export const BRANCH_BREACHES = Object.values(BRANCH_MEASURE)
  .filter(b => b.breachCount > 0 || b.slaAvg < 90)
  .sort((a, b) => b.breachCount - a.breachCount || a.slaAvg - b.slaAvg)
  .slice(0, 8);

// ---------- Achievement feed (ticker) ----------
const ACH_TEMPLATES = [
  (p) => `${shortName(p)} just unlocked ${BADGES[p.badges[0]]?.name ?? 'an achievement'} — ${p.townName}`,
  (p) => `${shortName(p)} hit ${p.kpis.targetPct}% of monthly target`,
  (p) => `${shortName(p)} extended SLA streak to ${p.streakDays} days — ${p.regionName}`,
  (p) => `${shortName(p)} filed all reports on time — 90 day mark`,
  (p) => `${shortName(p)} achieved CSAT ${p.kpis.csat}% this week`,
  (p) => `${shortName(p)} climbed ${Math.abs(p.deltaWeek)} places on the leaderboard`,
  (p) => `${p.branchId.replace(/-/g, ' ')} crowned best branch in ${p.regionName}`,
];

function shortName(p) {
  const [first, ...rest] = p.name.split(' ');
  return `${first} ${rest.map(r => r[0]).join('.')}.`;
}

export const ACHIEVEMENT_FEED = (() => {
  const rng = prng(hashSeed('achievement-feed'));
  const standout = byScoreDesc.slice(0, 40);
  const events = [];
  for (let i = 0; i < 30; i++) {
    const p = standout[Math.floor(rng() * standout.length)];
    const tpl = ACH_TEMPLATES[Math.floor(rng() * ACH_TEMPLATES.length)];
    const minutesAgo = Math.floor(rng() * 180) + 1;
    events.push({
      id: `ach-${i}`,
      personId: p.id,
      portraitUrl: p.portraitUrl,
      text: tpl(p),
      badgeColor: BADGES[p.badges[0]]?.color ?? '#FFB800',
      minutesAgo,
    });
  }
  return events.sort((a, b) => a.minutesAgo - b.minutesAgo);
})();

// ---------- Helpers ----------
export function getStaffByBranch(branchId) {
  return (STAFF_BY_BRANCH[branchId] || []).slice().sort((a, b) => b.score - a.score);
}

export function getStaff(personId) {
  return STAFF.find(p => p.id === personId);
}

export function branchMeasure(branchId) {
  return BRANCH_MEASURE[branchId];
}

export function scopeLabel(scope) {
  // scope = { level, id } where level in ['group','country','region','town','branch']
  if (!scope || scope.level === 'group') return 'PG Glass Africa';
  if (scope.level === 'country') return COUNTRY_MEASURE.find(c => c.id === scope.id)?.name ?? 'Country';
  if (scope.level === 'region')  return REGION_MEASURE.find(r => r.id === scope.id)?.name ?? 'Region';
  if (scope.level === 'town')    return TOWN_MEASURE.find(t => t.id === scope.id)?.name ?? 'Town';
  if (scope.level === 'branch')  return BRANCH_MEASURE[scope.id]?.name ?? 'Branch';
  return 'All';
}

export function staffInScope(scope) {
  if (!scope || scope.level === 'group') return STAFF;
  if (scope.level === 'country') {
    const name = COUNTRY_MEASURE.find(c => c.id === scope.id)?.name;
    return STAFF.filter(p => p.countryName === name);
  }
  if (scope.level === 'region') {
    const r = REGION_MEASURE.find(x => x.id === scope.id);
    if (!r) return STAFF;
    return STAFF.filter(p => p.regionName === r.name && p.countryName === r.country);
  }
  if (scope.level === 'town') {
    const t = TOWN_MEASURE.find(x => x.id === scope.id);
    if (!t) return STAFF;
    return STAFF.filter(p => p.townName === t.name && p.regionName === t.region && p.countryName === t.country);
  }
  if (scope.level === 'branch') {
    return STAFF_BY_BRANCH[scope.id] || [];
  }
  return STAFF;
}
